/**
 * Library reads and the cross-domain song operations.
 *
 * Generating a song touches four domains — it spends CREDITS, adds to SONGS,
 * shows a UI toast, and can stop the PLAYER on delete. That coordination lives
 * here rather than inside any one store.
 *
 * SIMULATED. No audio is produced. Generation is a JOB — `POST /generations` answers a
 * queued record and the client polls `GET /generations/:id` until it settles — and what
 * finishes is a fabricated Song with a null `audioUrl`. See src/api/generationApi.ts.
 *
 * WHICH MEANS IT CAN FAIL, and `createSong` below has a `catch` where it used to have only a
 * `finally`. Batch 7 §1.7: the old shape was a timer that always resolved, so there was no
 * failure for this funnel to report; a poll loop rejects when the job reports `failed`, when
 * the server has forgotten the id, and when the client stops waiting. See that catch for what
 * the user is told and for why nothing is refunded here.
 *
 * The whole flow still takes about 1100ms against the mock, which is shorter than the
 * processing state the product asks for, so createSong holds that state for a minimum of five
 * seconds (lib/processing.ts). It is the one funnel every creation path uses — the composer,
 * the Genre Mixer, Music DNA and Image Music — which is why the floor and the failure
 * handling both live here rather than in four screens.
 */
import { useMemo } from 'react';
import {
  audioEditApi,
  buildExportAudioRequest,
  hasStoredAudioFile,
  needsStoredAudioFinalization,
  storedAudioRequirementMessage,
} from '../api/audioEditApi';
import { audioUploadApi } from '../api/audioUploadApi';
import { apiErrorMessage, isPaymentRequired, isUnsupported } from '../api/errors';
import { generationApi } from '../api/generationApi';
import { songsApi } from '../api/songsApi';
import { USE_MOCK_API } from '../config';
import { warmGeneratedSongForPlayback } from './usePlayer';
import { useCredits } from './useCredits';
import type { LyriaImage } from '../api/lyria';
import type { AudioContainer } from '../lib/audioUpload';
import {
  PREMIUM_MUSIC_MODEL,
  STANDARD_MUSIC_MODEL,
  type MusicGenerationModel,
} from '../lib/musicGenerationModels';
import {
  buildLyricsFile,
  buildLyricsPrintDocument,
  buildSongDetailsFile,
  downloadAudioFromUrl,
  downloadFromUrl,
  downloadTextFile,
} from '../lib/download';
import { EXPORT_PROFILES } from '../lib/exportSize';
import { withProcessingFloor } from '../lib/processing';
import { openPrintWindow } from '../lib/printBrand';
import { MUSIC_GENERATION_CREDITS } from '../lib/credits';
import { useAuthStore } from '../stores/authStore';
import { selectedGenerationBonusPacks } from '../stores/bonusPackSelectionStore';
import { useCreditsStore } from '../stores/creditsStore';
import { useEntitlementsStore } from '../stores/entitlementsStore';
import { usePreferencesStore } from '../stores/preferencesStore';
import {
  filterSongs,
  selectSongById,
  type GenerationSurface,
  useSongsStore,
} from '../stores/songsStore';
import { usePlayerStore } from '../stores/playerStore';
import { usePlaylistsStore } from '../stores/playlistsStore';
import { useStudioProjectsStore } from '../stores/studioProjectsStore';
import { useStudioStore } from '../stores/studioStore';
import { useUiStore } from '../stores/uiStore';
import type { Song } from '../types/domain';

/**
 * Which creation path a generation came from.
 *
 * Declared by the caller rather than inferred, because two of these require a plan and the
 * funnel below cannot tell them apart from the request body: a Genre Mixer generation and
 * a hand-written prompt arrive as the same three arguments. 'composer' is the default, so
 * every existing call site keeps its exact behaviour by saying nothing.
 *
 * Image Music is not a member. It needs no plan — the base product includes it, and what
 * stops it today is a missing backend rather than an unbought upgrade — and it already
 * identifies itself by carrying `options.images`, which is what its own guard reads. A
 * member here that no call site passed would be a slot inviting a second rule about it.
 */
export type CreationMethod = 'composer' | 'dna' | 'mixer';

/**
 * The plan each creation method requires.
 *
 * ONE TABLE RATHER THAN AN `if` PER METHOD, because the entitlement rule is product
 * definition — Lyrixa Composer+ lists "Music DNA Analysis" and "Genre Mixer" as the two
 * things it sells (data/products.ts) — and a table is a thing you can read against that
 * catalogue. Methods absent from it need nothing.
 *
 * The value is the entitlement field's own name, so the lookup below indexes the store
 * directly and no mapping from a method to a flag has to be maintained anywhere else.
 *
 * Exported so a test can assert the rule directly rather than only through a screen.
 */
export const METHOD_ENTITLEMENT: Partial<Record<CreationMethod, 'composerPlus'>> = {
  dna: 'composerPlus',
  mixer: 'composerPlus',
};

/** What the refusal says. Names the plan and the feature, so it is actionable. */
export const COMPOSER_PLUS_REQUIRED_MESSAGE =
  'Music DNA and Genre Mixer require Lyrixsa Composer+ — no credits were used.';

export const GENERATED_SONG_REFRESH_TIMEOUT_MS = 60_000;
export const GENERATED_SONG_REFRESH_INTERVAL_MS = 2_000;
const refreshingGeneratedSongs = new Set<string>();

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function generatedSongNeedsStoredAudio(song: Song): boolean {
  return needsStoredAudioFinalization(song);
}

export function shouldRefreshGeneratedSong(song: Song): boolean {
  /*
    Cover generation has already either succeeded or failed by the time the generation job
    returns a Song. A missing cover is therefore a final fallback-art state, not unfinished
    work. Treating it as unfinished made every hydration poll every coverless generated song
    for a full minute, overwhelming the web service with unrelated GET /songs/:id requests.

    Provider audio is different: the backend deliberately returns it before its background
    R2 copy finishes, then changes audioUrl to the stored-media route. That is the only Song
    field this client needs to reconcile after generation.
  */
  return generatedSongNeedsStoredAudio(song);
}

export async function reconcileGeneratedSong(song: Song): Promise<void> {
  if (!shouldRefreshGeneratedSong(song) || refreshingGeneratedSongs.has(song.id)) return;

  refreshingGeneratedSongs.add(song.id);
  const startedAt = Date.now();
  try {
    while (Date.now() - startedAt < GENERATED_SONG_REFRESH_TIMEOUT_MS) {
      await sleep(GENERATED_SONG_REFRESH_INTERVAL_MS);
      const current = useSongsStore.getState().songs.find((candidate) => candidate.id === song.id);
      if (!current) return;

      const needsStoredAudio = generatedSongNeedsStoredAudio(current);
      if (!needsStoredAudio) return;

      const fresh = await songsApi.getSong(song.id);
      if (!fresh) return;

      const freshNeedsStoredAudio = generatedSongNeedsStoredAudio(fresh);
      const coverChanged = fresh.coverUrl !== current.coverUrl;
      const audioChanged = fresh.audioUrl !== current.audioUrl;

      if (coverChanged || audioChanged) {
        useSongsStore.getState().replaceSong(fresh);
      }

      if (!freshNeedsStoredAudio) return;
    }
    console.warn('[generation] song-refresh:timed-out', {
      songId: song.id,
      waitingForStoredAudio: generatedSongNeedsStoredAudio(song),
    });
  } catch (error) {
    console.warn('[generation] song-refresh:failed', {
      songId: song.id,
      message: error instanceof Error ? error.message : String(error),
    });
  } finally {
    refreshingGeneratedSongs.delete(song.id);
  }
}

export function useSongs() {
  const songs = useSongsStore((s) => s.songs);
  const filters = useSongsStore((s) => s.filters);
  const generating = useSongsStore((s) => s.generating);
  const activeGenerationSurface = useSongsStore((s) => s.activeGenerationSurface);
  const setFilters = useSongsStore((s) => s.setFilters);
  const addSong = useSongsStore((s) => s.addSong);
  const setGenerating = useSongsStore((s) => s.setGenerating);
  const setActiveGenerationSurface = useSongsStore((s) => s.setActiveGenerationSurface);
  const nextSeq = useSongsStore((s) => s.nextSeq);
  const updateSong = useSongsStore((s) => s.updateSong);
  const deleteSongRaw = useSongsStore((s) => s.deleteSong);
  const duplicateSongRaw = useSongsStore((s) => s.duplicateSong);
  const toggleFavorite = useSongsStore((s) => s.toggleFavorite);
  const setVisibility = useSongsStore((s) => s.setVisibility);
  const recordDownload = useSongsStore((s) => s.recordDownload);
  const exportFormat = usePreferencesStore((s) => s.exportFormat);

  const stopIfPlaying = usePlayerStore((s) => s.stopIfPlaying);
  const removeSongEverywhere = usePlaylistsStore((s) => s.removeSongEverywhere);
  // The same cleanup, for the same reason, over Studio projects: a project holds ids and
  // a deleted one has nothing to resolve to, so it would sit in the array for ever and
  // silently shorten the project's own count. See deleteSong below.
  const removeSongFromProjects = useStudioProjectsStore((s) => s.removeSongEverywhere);
  const studioSongId = useStudioStore((s) => s.songId);
  const closeStudio = useStudioStore((s) => s.closeStudio);
  const showToast = useUiStore((s) => s.showToast);
  const setOutOfCreditsOpen = useUiStore((s) => s.setOutOfCreditsOpen);
  const { spend } = useCredits();

  const filteredSongs = useMemo(() => filterSongs(songs, filters), [songs, filters]);

  return {
    songs,
    filteredSongs,
    filters,
    setFilters,
    generating,
    activeGenerationSurface,
    toggleFavorite,
    updateSong,

    /** Looks up a song by id, for route params. */
    getSong(id: string | undefined): Song | null {
      return selectSongById(songs, id ?? null);
    },

    /**
     * Generates a song.
     *
     * Starts generation first and only then charges once a finished song exists:
     * a failed generation leaves the balance untouched, and a short balance after
     * generation returns null before anything is added to the library. Returns the new Song, or
     * null when the balance was short (the out-of-credits modal is raised by
     * `spend`), when the prompt was empty, when the method needs a plan the account
     * does not own, or WHEN THE GENERATION ITSELF FAILED — the last of those is new
     * with Batch 7 §1.7 and is toasted before it returns. One value for "no song was
     * made" rather than a shape each caller has to destructure, because what every
     * call site does with it is the same: stop, and do not navigate to a result.
     *
     * `vocal` and `language` are optional: the composer passes the user's choices,
     * while the Genre Mixer and Music DNA generate without asking for either — the
     * mixer states both inside the prompt it builds (lib/music.buildMixerPrompt).
     *
     * `options.images` is the Image Music path, and it is the same funnel on purpose:
     * one place charges the credit, holds the five-second processing state, adds the
     * song and toasts. What differs is only what goes in the request body, which is
     * generationApi's business rather than this hook's.
     *
     * `options.method` says which creation path this is, and the two that Composer+ sells
     * are refused here without charging — see the guard below.
     */
    async createSong(
      promptText: string,
      genreName: string,
      cost = MUSIC_GENERATION_CREDITS,
      vocal?: string,
      language?: string,
      options?: {
        images?: readonly LyriaImage[];
        method?: CreationMethod;
        ownerSurface?: GenerationSurface;
        model?: MusicGenerationModel;
      }
    ): Promise<Song | null> {
      const images = options?.images ?? [];
      const ownerSurface = options?.ownerSurface ?? null;
      if (!promptText.trim() && images.length === 0) return null;

      /*
        NO PLAN, NO GENERATION — AND NO CHARGE.

        Before spend() and before setGenerating(), which is the whole point of it being
        here: the task requires that an account without Composer+ is not charged a credit
        and that no generation process starts, and a check inside the two screens could
        not promise either. This is the one funnel all four creation paths go through, so
        the rule holds however the call was reached — including from a devtools-flipped
        route gate, which is exactly the bypass the route-level check cannot cover.

        The route gate in src/router.tsx is what a user actually meets; it replaces both
        screens with the upgrade prompt, so in normal use this guard never fires. It is
        the same relationship publisherApi.submitRelease has with RequirePublisher: the
        screen is the explanation, the funnel is the rule.

        Read with getState() rather than a selector, because this is a decision made at
        call time and not something rendered. A subscription here would re-run every
        consumer of this hook — the Library, the composer, the workspace — on an
        entitlement change, to answer a question none of them asks while idle.
      */
      const required = METHOD_ENTITLEMENT[options?.method ?? 'composer'];
      if (required && !useEntitlementsStore.getState()[required]) {
        showToast(COMPOSER_PLUS_REQUIRED_MESSAGE);
        return null;
      }

      const model = options?.model ?? STANDARD_MUSIC_MODEL;
      if (model === PREMIUM_MUSIC_MODEL && !useEntitlementsStore.getState().professional) {
        showToast('Lyx3pro requires Lyrixsa AI Professional (OTO 1).');
        return null;
      }

      /*
        Nothing is charged for a request that cannot be made.

        Checked BEFORE spend, not caught after it: in mock/test mode Image Music has no
        Lyrixa backend to post to, so taking a credit and then failing would charge for a
        certainty. In runtime/backend mode this guard clears and the backend becomes the
        authority — including fail-fast refusals when its own Lyria 3 credentials are not
        configured. The screen already says the frontend-side limitation up front; this is
        the guard for a caller that did not look.
      */
      if (images.length > 0) {
        const support = generationApi.imageMusicSupport();
        if (!support.available) {
          showToast(support.blockers[0]?.summary ?? 'Image Music is not available yet');
          return null;
        }
      }

      /*
        THE PROCESSING STATE GOES UP BEFORE THE REQUEST. In backend mode the server atomically
        reserves the workflow charge before accepting the job, then commits it only when the song
        succeeds. Mock mode has no server ledger, so it retains the local post-success spend.

        Set before the `try` so the `finally` below still covers everything from here on.
      */
      setGenerating(true);
      setActiveGenerationSurface(ownerSurface);
      try {
        /*
          The account the song will be attributed to.

          Resolved before the request rather than inside it, so the value is the session as
          it was when the user pressed Create — a sign-out mid-generation cannot leave the
          finished song crediting nobody.

          `user.name` and not the `selectProfileName` selector, which falls back to a
          display name for an unauthenticated read. That fallback is right for a header
          that must render something; it is wrong here, because it would stamp a real
          person's name onto a song generated with no session. Absent is the honest value.

          Read after the charge, so it is the session at the moment the request is actually
          made rather than at the moment the button was pressed.
        */
        const authUser = useAuthStore.getState().user;
        const creator = authUser ? { id: authUser.id, name: authUser.name } : undefined;
        const source =
          images.length > 0
            ? 'image-music'
            : options?.method === 'dna'
              ? 'music-dna'
              : options?.method === 'mixer'
                ? 'genre-mixer'
                : 'text-music';

        /*
          The processing state is held for a minimum of PROCESSING_MS.

          Here rather than in each screen, because this is the one funnel all three
          creation paths go through — the composer, the Genre Mixer and Music DNA — so
          the wait is the same length wherever a song comes from, and no screen can
          forget to have one.

          It stretches how long a genuine wait is watched; it does not fake progress or
          delay a result that exists. The request is already in flight when the floor
          starts, both are awaited, and the later of the two decides — so once real
          generation takes longer than the floor this stops having any effect. A
          rejection is not held back at all. See lib/processing.ts.
        */
        const song = await withProcessingFloor(
          generationApi.createGeneration({
            prompt: promptText,
            bonusPacks: selectedGenerationBonusPacks(),
            genre: genreName,
            vocal,
            language,
            model,
            source,
            cost,
            seq: nextSeq(),
            /*
              WHO IS GENERATING, so the finished song can be attributed on the catalogue
              (Batch 7 §17). Read from the auth store rather than passed in by the four
              call sites: the creator is the signed-in account by definition, and a
              parameter would be a chance for a screen to name a different one.

              getState() rather than a selector, for the reason the entitlement guard
              above gives — this is read at call time, and subscribing would re-render
              the Library and the composer whenever the profile changed.

              `undefined` WHEN NOBODY IS SIGNED IN, and no fallback name. The API's own
              header explains why this parameter exists at all and why it must never
              become part of the real request body: the backend derives the creator from
              the session. See lib/attribution for why absent beats a placeholder.
            */
            ...(creator ? { creator } : null),
            // Absent for every text-only caller, so their request body is byte-for-byte
            // what it was before Image Music existed.
            ...(images.length > 0 ? { images } : null),
          })
        );
        if (USE_MOCK_API) {
          if (!(await spend(cost, 'Song generation'))) return null;
        } else {
          // The generation job commits its reservation server-side. Refresh that result;
          // issuing a second spend here would charge the same song twice.
          await useCreditsStore.getState().loadBalance();
        }
        addSong(song);
        if (shouldRefreshGeneratedSong(song)) {
          void reconcileGeneratedSong(song);
        }
        warmGeneratedSongForPlayback(song);
        usePlayerStore.getState().play(song.id);
        showToast('Song generated — added to your library');
        return song;
      } catch (err) {
        /*
          ==========================================================================
          A GENERATION CAN FAIL NOW, AND THIS IS WHERE THAT IS SAID OUT LOUD.

          Before Batch 7 §1.7 `createGeneration` was a timer that always resolved, so this
          funnel had a `finally` and no `catch` — there was no failure to handle. It is a poll
          loop now (api/generationApi), and it rejects for three distinct reasons: the job
          reported `failed`, the job is no longer known to the server, or this client stopped
          waiting. Without this block those became an unhandled rejection: the wave came down
          and the screen said nothing at all, which is the worst of the available outcomes —
          the user cannot tell a failure from a click that did not register.

          `apiErrorMessage` rather than a sentence of our own, because that is the one error
          contract (api/errors.ts) and it already knows what each of those cases should read
          as: a failed job keeps the provider's own reason, a 504 keeps
          GENERATION_TIMEOUT_MESSAGE — which deliberately does NOT claim the generation
          failed — and a capability refusal keeps the text written by whoever knew what was
          missing. `preserveMessage: false` covers the fifth case, a genuine bug, whose
          message is internals rather than something to show someone waiting for a song. Same
          call and same reasoning as supportStore.submit.

          NOTHING IS REFUNDED HERE, AND THAT IS DELIBERATE. The credit was spent through
          creditsStore.spend, where the server is the authority (§1.4) — so a client that
          handed itself credits back on a failure would be doing precisely the thing that
          store's rollback is careful not to do: deciding its own balance. The rollback there
          is for a spend the API REJECTED, which is a different fact from work that was paid
          for and did not come out.

          BACKEND DEPENDENCY, and it belongs in the handoff rather than in a local
          adjustment: whether a failed generation is charged for is a server policy. The
          contract already prefers debiting credits as a side effect of the operation that
          costs them rather than through a separate `POST /credits/spend` the client makes
          first (docs/api-contract.md §21) — under which a job that never produced a track
          was never charged, and there is nothing to refund. Until that endpoint exists there
          is no authority to ask, and inventing an answer locally would be a fabricated
          balance.
          ==========================================================================
        */
        if (isUnsupported(err)) showToast(apiErrorMessage(err));
        else if (isPaymentRequired(err)) setOutOfCreditsOpen(true);
        else showToast(apiErrorMessage(err, { preserveMessage: false }));
        // `null` is this function's existing word for "no song was made", and every call
        // site already branches on it — see MusicDNA and the Genre Mixer, which stop
        // rather than navigating. So a failure needs no new shape at the boundary.
        return null;
      } finally {
        // Cleared in `finally` so neither a refused charge nor a rejected generation can
        // leave the Create button stuck in its loading state.
        setGenerating(false);
        setActiveGenerationSurface(null);
      }
    },

    /**
     * Uploads a user's own audio file and adds it to the library as a new song.
     *
     * No credits, no processing floor, no plan gate — this is not a generation, it is a
     * file the user already has, so the only things that can go wrong are "there is
     * nowhere to send it yet" (`AudioUploadUnavailableError`, when no backend is
     * configured) and a genuine transport/server failure. Both are toasted with
     * `apiErrorMessage` and return `null`, the same "no song was made" contract
     * `createSong` uses, so `UploadPanel` needs no bespoke error handling of its own.
     */
    async uploadAudioFile(input: {
      file: File;
      container: AudioContainer;
      title: string;
      artistName: string;
      genre: string;
      artistGender: string;
      uploaderName: string;
    }): Promise<Song | null> {
      try {
        const song = await audioUploadApi.upload(input);
        addSong(song);
        showToast(`"${song.title}" uploaded — added to your library`);
        return song;
      } catch (err) {
        showToast(apiErrorMessage(err, { preserveMessage: true }));
        return null;
      }
    },

    /**
     * Deletes a song, stopping playback and closing the Studio if it was open.
     *
     * Also leaves every playlist AND every Studio project. Both hold ids and resolve
     * them against the library on read, which is what makes a rename or a new cover
     * appear everywhere for free — but a deleted id has nothing to resolve to, so it
     * would sit in the array for ever, silently shortening the playlist's count and its
     * play order, and the project's track count. This is the single funnel every delete
     * goes through, so the cleanup belongs here rather than in a store reaching across
     * into another one.
     */
    deleteSong(song: Song) {
      deleteSongRaw(song.id);
      stopIfPlaying(song.id);
      removeSongEverywhere(song.id);
      removeSongFromProjects(song.id);
      if (studioSongId === song.id) closeStudio();
      showToast(`"${song.title}" deleted`);
    },

    async duplicateSong(song: Song) {
      try {
        const copy = await duplicateSongRaw(song);
        showToast(`Duplicated "${song.title}"`);
        return copy;
      } catch (err) {
        showToast(apiErrorMessage(err, { preserveMessage: true }));
        return null;
      }
    },

    /**
     * Lists a song in the Lyrixa Catalogue, after its owner confirmed the dialog.
     *
     * THROUGH `songsStore.setVisibility`, WHICH IS THE POINT. That action is the single
     * path visibility changes on, so this is a thin wrapper adding the two things a
     * store must not own: the READY guard and the toast.
     *
     * The guard is here rather than only in the components that offer the action. The
     * Library hides the control for a draft, a generating and a failed song, but "the
     * button was not rendered" is a UI fact — this is the funnel every caller goes
     * through, so an unfinished song cannot be listed however the call was reached. The
     * same relationship `createSong`'s entitlement check has with the route gate above.
     *
     * Refused silently rather than toasted: there is no user gesture to explain, because
     * no surface offers the action in that state. A message would describe a button
     * nobody pressed.
     *
     * AWAITS THE STORE'S ANSWER NOW, rather than firing `setVisibility` and declaring
     * victory on the spot. `setVisibility` sets optimistically and rolls back if the
     * server rejects the PATCH (an expired session, a lapsed purchase, ...) — this is the
     * one caller that must tell the difference, because it is the caller that hands the
     * dialog a link to show. A success toast over a publication that never actually
     * happened would send someone off to share a URL that 404s for everyone but them.
     */
    async makeDiscoverable(song: Song) {
      if (song.status !== 'ready') return;
      const persisted = await setVisibility(song.id, 'public');
      if (persisted) {
        showToast(`"${song.title}" is discoverable on Lyrixsa`);
      } else {
        showToast('Could not make this track discoverable — try again in a moment.');
      }
    },

    /**
     * Takes a song back out of the Lyrixa Catalogue.
     *
     * `'private'` rather than `'unlisted'`: unlisted means "not listed, but reachable by
     * anyone holding the link", which is a third state with its own meaning
     * (api/shareApi.isLinkAccessible) and not what "remove from the catalogue" asks for.
     * Removing a listing returns the song to the owner-only state it was made in.
     *
     * ONLY `visibility` MOVES. No delete, no audio, no lyrics, no artwork, no versions,
     * no playlist membership — `setVisibility` patches exactly one field, which is what
     * makes that promise the dialog prints checkable rather than aspirational.
     */
    removeFromCatalogue(song: Song) {
      void setVisibility(song.id, 'private').then((persisted) => {
        if (persisted) {
          showToast(`"${song.title}" removed from the Lyrixsa Catalogue`);
        } else {
          showToast('Could not remove this track from the catalogue — try again in a moment.');
        }
      });
    },

    /**
     * Downloads a song — the real file when there is one, its details when there is not.
     *
     * ========================================================================
     * TWO BRANCHES, AND TODAY ONLY THE SECOND RUNS (Batch 7 §10). The gate is
     * `audioEditApi.supports('exportAudio')`, which is hardcoded false, so every press
     * still writes the same text details file it wrote before and says so. Nothing about
     * what a user gets has changed.
     *
     * The first branch is the point: §10 asks for a clean boundary for receiving an audio
     * download URL, and this is the one call site an export would arrive at. It builds the
     * real request (`buildExportAudioRequest`), sends it through the API boundary, and hands
     * the returned URL to `downloadFromUrl` — so the day the endpoint exists this function
     * needs no edit and neither does any of its five call sites. Before this, the seam
     * existed and nothing referenced it, which is indistinguishable from not having one.
     * ========================================================================
     *
     * `requestedFormat` records what was asked for ("MP3"/"WAV") so the placeholder's
     * filename and toast can be honest about the substitution rather than handing over a
     * .txt named like a track. `quality` is the Download Center's selected tier; the
     * composer's plain Download button passes neither and gets the details file it always
     * got.
     *
     * NOT A FALLBACK FROM A FAILURE. The placeholder is chosen because the capability is
     * unsupported, checked before anything is sent — not in a `catch` around a request that
     * failed. A caught refusal is still handled below, because an unguarded throw would
     * leave the user with no file and no message, but it is the unreachable path and the
     * message names it as unavailable rather than as an error.
     */
    async downloadSong(song: Song, requestedFormat?: string, quality?: string) {
      const detailsOnly = requestedFormat === 'DETAILS_ONLY';
      const exportRequestFormat = detailsOnly ? undefined : requestedFormat;
      const format = exportRequestFormat
        ? exportRequestFormat.toLowerCase() === 'wav'
          ? 'wav'
          : 'mp3'
        : exportFormat.toLowerCase() === 'wav'
          ? 'wav'
          : 'mp3';

      if (hasStoredAudioFile(song) && !detailsOnly) {
        try {
          const url = `/api/media/audio/${song.id}/export?format=${format}`;
          downloadAudioFromUrl(url, song.title, format);
          recordDownload(song.id, exportRequestFormat ?? format.toUpperCase());
          showToast(`Downloading "${song.title}" — ${format.toUpperCase()}`);
          return;
        } catch (err) {
          showToast(apiErrorMessage(err, { preserveMessage: true }));
          return;
        }
      }

      if (!detailsOnly && needsStoredAudioFinalization(song)) {
        showToast(storedAudioRequirementMessage(song, 'download the audio file'));
        return;
      }

      if (exportRequestFormat && audioEditApi.supports('exportAudio')) {
        const request = buildExportAudioRequest({
          songId: song.id,
          format: exportRequestFormat,
          quality: quality ?? EXPORT_PROFILES[0].id,
        });
        try {
          const result = await audioEditApi.exportAudio(request);
          downloadFromUrl(result.url, result.filename);
          recordDownload(song.id, exportRequestFormat);
          showToast(`Downloading "${song.title}" — ${result.format.toUpperCase()}`);
          return;
        } catch (err) {
          // `apiErrorMessage` rather than a sentence of our own: `UnsupportedCapabilityError`
          // maps to 501 with its own message preserved (api/errors.ts), and that message
          // already names the missing capability better than this hook could.
          showToast(apiErrorMessage(err, { preserveMessage: true }));
          return;
        }
      }

      const { filename, contents } = buildSongDetailsFile(song, exportRequestFormat);
      downloadTextFile(filename, contents);
      recordDownload(song.id, exportRequestFormat);
      showToast(
        exportRequestFormat
          ? `Saved details for "${song.title}" — ${exportRequestFormat} audio export coming soon`
          : `Saved details for "${song.title}"`
      );
    },

    downloadLyrics(song: Song) {
      const { filename, contents } = buildLyricsFile(song);
      downloadTextFile(filename, contents);
      showToast('Downloading lyrics');
    },

    /**
     * Opens a print window with the song's lyric sheet.
     *
     * The document is a branded A4 lyric sheet (lib/download.buildLyricsPrintDocument,
     * on lib/printBrand's page chrome) rather than the bare `<pre>` this used to write.
     * Escaping happens inside the builder — see buildPrintDocument's header for which
     * field is the caller's responsibility.
     */
    printLyrics(song: Song) {
      // Popup blockers make window.open return null. This used to fail silently,
      // leaving the user with no idea why nothing happened.
      if (!openPrintWindow(buildLyricsPrintDocument(song))) {
        showToast('Allow pop-ups for this site to print lyrics');
      }
    },
  };
}
